// ─── Types ────────────────────────────────────────────────────────────────────

export interface UserLevel {
  level: number;       // 1–50
  title: string;       // e.g., "학습 새싹", "성장 탐험가"
  currentXP: number;   // XP accumulated within the current level
  requiredXP: number;  // XP needed to reach the next level
  totalXP: number;     // Lifetime accumulated XP
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;          // emoji
  earnedAt?: string;     // ISO date string; undefined = not yet earned
  category: 'survey' | 'checkin' | 'goal' | 'streak' | 'special';
}

export interface XPRecord {
  amount: number;
  source: XPSource;
  description: string;
  earnedAt: string; // ISO date string
}

export interface GamificationState {
  level: UserLevel;
  badges: Badge[];
  streaks: {
    checkin: number;  // consecutive check-in days
    goal: number;     // consecutive goal-completion days
  };
  xpHistory: XPRecord[];
}

export type XPSource =
  | 'survey_complete'
  | 'checkin'
  | 'goal_task_done'
  | 'milestone_complete'
  | 'vision_complete'
  | 'goal_review'
  | 'streak_milestone'
  | 'badge_earned';

// ─── Constants ────────────────────────────────────────────────────────────────

/** XP awarded per activity */
export const XP_VALUES: Record<XPSource, number> = {
  survey_complete: 100,
  checkin: 20,
  goal_task_done: 30,
  milestone_complete: 50,
  vision_complete: 200,
  goal_review: 40,
  streak_milestone: 30,
  badge_earned: 50,
};

export const XP_SOURCE_LABELS: Record<XPSource, string> = {
  survey_complete: '설문 완료',
  checkin: '일일 체크인',
  goal_task_done: '목표 달성',
  milestone_complete: '마일스톤 완료',
  vision_complete: '장기 목표 달성',
  goal_review: '목표 회고',
  streak_milestone: '습관 스트릭',
  badge_earned: '배지 획득',
};

/** Level title bands */
const LEVEL_TITLES: Array<{ min: number; max: number; title: string }> = [
  { min: 1,  max: 5,  title: '성장 새싹'      },
  { min: 6,  max: 10, title: '성장 탐험가'     },
  { min: 11, max: 20, title: '지식 수호자'     },
  { min: 21, max: 30, title: '자기관리 달인'   },
  { min: 31, max: 40, title: '성장 마스터'     },
  { min: 41, max: 50, title: '성찰의 현자'   },
];

/** XP required to reach *next* level from current level.
 *  Formula: base 100 XP at level 1, +25 XP per level. */
function xpForLevel(level: number): number {
  return 100 + (level - 1) * 25;
}

/** Cumulative XP needed to reach a given level from level 1. */
function cumulativeXPForLevel(level: number): number {
  let total = 0;
  for (let l = 1; l < level; l++) {
    total += xpForLevel(l);
  }
  return total;
}

const STORAGE_KEY = 'gamification_state';
const MAX_LEVEL = 50;

// ─── All Badge Definitions ────────────────────────────────────────────────────

const ALL_BADGE_DEFINITIONS: Omit<Badge, 'earnedAt'>[] = [
  // Survey
  {
    id: 'survey_first',
    name: '첫 진단 완료',
    description: '자기점검 설문을 처음으로 완료했어요.',
    icon: '🧪',
    category: 'survey',
  },
  {
    id: 'survey_3times',
    name: '3회 진단',
    description: '자기점검 설문을 3번 완료했어요.',
    icon: '🔬',
    category: 'survey',
  },
  {
    id: 'survey_all_sets',
    name: '모든 세트 완료',
    description: '모든 설문 세트를 완료했어요.',
    icon: '🏆',
    category: 'survey',
  },

  // Check-in
  {
    id: 'checkin_first',
    name: '첫 체크인',
    description: '처음으로 일일 체크인을 완료했어요.',
    icon: '✅',
    category: 'checkin',
  },
  {
    id: 'checkin_7days',
    name: '7일 연속 체크인',
    description: '7일 연속으로 체크인했어요.',
    icon: '🗓️',
    category: 'checkin',
  },
  {
    id: 'checkin_30days',
    name: '30일 연속 체크인',
    description: '30일 연속으로 체크인했어요.',
    icon: '📅',
    category: 'checkin',
  },
  {
    id: 'checkin_100days',
    name: '100일 연속 체크인',
    description: '100일 연속으로 체크인했어요. 정말 대단해요!',
    icon: '💯',
    category: 'checkin',
  },

  // Goal
  {
    id: 'goal_first',
    name: '첫 목표 달성',
    description: '처음으로 목표를 달성했어요.',
    icon: '🎯',
    category: 'goal',
  },
  {
    id: 'goal_5times',
    name: '5개 목표 달성',
    description: '목표를 5개 달성했어요.',
    icon: '⭐',
    category: 'goal',
  },
  {
    id: 'goal_10times',
    name: '10개 목표 달성',
    description: '목표를 10개 달성했어요.',
    icon: '🌟',
    category: 'goal',
  },

  // Streak
  {
    id: 'streak_3days',
    name: '3일 연속 학습',
    description: '3일 연속으로 학습 활동을 했어요.',
    icon: '🔥',
    category: 'streak',
  },
  {
    id: 'streak_7days',
    name: '7일 연속 학습',
    description: '7일 연속으로 학습 활동을 했어요.',
    icon: '🌈',
    category: 'streak',
  },
  {
    id: 'streak_30days',
    name: '30일 연속 학습',
    description: '30일 연속으로 학습 활동을 했어요.',
    icon: '🚀',
    category: 'streak',
  },

  // Goal - Phase 1 additions
  {
    id: 'goal_first_vision',
    name: '첫 장기 목표',
    description: '처음으로 장기 목표를 만들었어요.',
    icon: '🏔️',
    category: 'goal',
  },
  {
    id: 'goal_first_milestone',
    name: '첫 마일스톤 달성',
    description: '첫 번째 마일스톤을 완료했어요.',
    icon: '🗻',
    category: 'goal',
  },
  {
    id: 'goal_vision_complete',
    name: '장기 목표 달성',
    description: '장기 목표를 끝까지 완주했어요!',
    icon: '🏅',
    category: 'goal',
  },
  {
    id: 'goal_first_review',
    name: '첫 회고',
    description: '처음으로 목표 회고를 작성했어요.',
    icon: '📝',
    category: 'goal',
  },

  // Streak - Phase 1 habit additions
  {
    id: 'streak_habit_7days',
    name: '7일 습관 스트릭',
    description: '반복 목표를 7일 연속 달성했어요.',
    icon: '🔥',
    category: 'streak',
  },
  {
    id: 'streak_habit_30days',
    name: '30일 습관 마스터',
    description: '반복 목표를 30일 연속 달성했어요!',
    icon: '💎',
    category: 'streak',
  },

  // Special
  {
    id: 'special_high_level',
    name: '성장가',
    description: '레벨 20 이상 달성! 진정한 성장가예요.',
    icon: '👑',
    category: 'special',
  },
  {
    id: 'special_ai_chat',
    name: 'AI 코치 첫 대화',
    description: 'AI 코치와 처음으로 대화했어요.',
    icon: '🤖',
    category: 'special',
  },
  {
    id: 'special_profile_complete',
    name: '프로필 완성',
    description: '프로필을 완성했어요.',
    icon: '🪪',
    category: 'special',
  },
];

// ─── Level Calculation ────────────────────────────────────────────────────────

/** Compute a full UserLevel descriptor from a lifetime XP total.
 * @param levelCap Optional tier-based level cap (default: MAX_LEVEL=50). Free=20, Plus/Pro=50.
 */
export function calculateLevel(totalXP: number, levelCap?: number): UserLevel {
  const cap = levelCap ?? MAX_LEVEL;
  const xp = Math.max(0, totalXP);
  let level = 1;

  // Walk up levels until we can't afford the next one
  while (level < cap) {
    const needed = xpForLevel(level);
    const base = cumulativeXPForLevel(level);
    if (xp >= base + needed) {
      level++;
    } else {
      const currentXP = xp - cumulativeXPForLevel(level);
      const requiredXP = xpForLevel(level);
      const band = LEVEL_TITLES.find((b) => level >= b.min && level <= b.max);
      return {
        level,
        title: band?.title ?? '성찰의 현자',
        currentXP: Math.max(0, currentXP),
        requiredXP,
        totalXP: xp,
      };
    }
  }

  // MAX level reached
  const band = LEVEL_TITLES.find((b) => cap >= b.min && cap <= b.max);
  return {
    level: cap,
    title: band?.title ?? '성찰의 현자',
    currentXP: xpForLevel(cap),
    requiredXP: xpForLevel(cap),
    totalXP: xp,
  };
}

// ─── Badge Eligibility ────────────────────────────────────────────────────────

export type BadgeAction =
  | { type: 'survey_complete'; surveyCount: number; allPersonasCompleted: boolean }
  | { type: 'checkin'; checkinStreak: number; totalCheckins: number }
  | { type: 'goal_task_done'; goalCompletionCount: number }
  | { type: 'streak_update'; learningStreak: number }
  | { type: 'level_up'; newLevel: number }
  | { type: 'ai_chat' }
  | { type: 'profile_complete' }
  | { type: 'vision_created' }
  | { type: 'milestone_complete'; milestoneCount: number }
  | { type: 'vision_complete' }
  | { type: 'goal_review' }
  | { type: 'habit_streak'; habitStreak: number };

/**
 * Given the current state and a triggering action, returns any newly-earned badges.
 * Only badges that have not already been earned are returned.
 */
export function checkBadgeEligibility(
  state: GamificationState,
  action: BadgeAction,
): Badge[] {
  const alreadyEarned = new Set(
    state.badges.filter((b) => b.earnedAt !== undefined).map((b) => b.id),
  );
  const now = new Date().toISOString();
  const newBadges: Badge[] = [];

  const earn = (id: string): void => {
    if (alreadyEarned.has(id)) return;
    const def = ALL_BADGE_DEFINITIONS.find((b) => b.id === id);
    if (!def) return;
    newBadges.push({ ...def, earnedAt: now });
  };

  switch (action.type) {
    case 'survey_complete': {
      if (action.surveyCount >= 1) earn('survey_first');
      if (action.surveyCount >= 3) earn('survey_3times');
      if (action.allPersonasCompleted) earn('survey_all_sets');
      break;
    }
    case 'checkin': {
      if (action.totalCheckins >= 1) earn('checkin_first');
      if (action.checkinStreak >= 7) earn('checkin_7days');
      if (action.checkinStreak >= 30) earn('checkin_30days');
      if (action.checkinStreak >= 100) earn('checkin_100days');
      break;
    }
    case 'goal_task_done': {
      if (action.goalCompletionCount >= 1) earn('goal_first');
      if (action.goalCompletionCount >= 5) earn('goal_5times');
      if (action.goalCompletionCount >= 10) earn('goal_10times');
      break;
    }
    case 'streak_update': {
      if (action.learningStreak >= 3) earn('streak_3days');
      if (action.learningStreak >= 7) earn('streak_7days');
      if (action.learningStreak >= 30) earn('streak_30days');
      break;
    }
    case 'level_up': {
      if (action.newLevel >= 20) earn('special_high_level');
      break;
    }
    case 'ai_chat': {
      earn('special_ai_chat');
      break;
    }
    case 'profile_complete': {
      earn('special_profile_complete');
      break;
    }
    case 'vision_created': {
      earn('goal_first_vision');
      break;
    }
    case 'milestone_complete': {
      if (action.milestoneCount >= 1) earn('goal_first_milestone');
      break;
    }
    case 'vision_complete': {
      earn('goal_vision_complete');
      break;
    }
    case 'goal_review': {
      earn('goal_first_review');
      break;
    }
    case 'habit_streak': {
      if (action.habitStreak >= 7) earn('streak_habit_7days');
      if (action.habitStreak >= 30) earn('streak_habit_30days');
      break;
    }
  }

  return newBadges;
}

// ─── Add XP ───────────────────────────────────────────────────────────────────

/**
 * Applies an XP gain to the current state, updates the level, appends to history,
 * and records badge XP bonuses if new badges were awarded.
 */
export function addXP(
  currentState: GamificationState,
  amount: number,
  source: XPSource,
  newBadges: Badge[] = [],
): GamificationState {
  const baseXP = Math.max(0, amount);
  const badgeBonus = newBadges.length * XP_VALUES.badge_earned;
  const totalGain = baseXP + badgeBonus;

  const newTotalXP = currentState.level.totalXP + totalGain;
  const newLevel = calculateLevel(newTotalXP);

  const now = new Date().toISOString();
  const newHistory: XPRecord[] = [
    {
      amount: baseXP,
      source,
      description: XP_SOURCE_LABELS[source],
      earnedAt: now,
    },
    ...newBadges.map((b) => ({
      amount: XP_VALUES.badge_earned,
      source: 'badge_earned' as XPSource,
      description: `배지 획득: ${b.name}`,
      earnedAt: now,
    })),
    ...currentState.xpHistory,
  ].slice(0, 100); // keep only the most recent 100 records

  // Merge new badges into existing badge list (replace unearned with earned)
  const earnedIds = new Set(newBadges.map((b) => b.id));
  const mergedBadges = currentState.badges.map((b) =>
    earnedIds.has(b.id) ? (newBadges.find((nb) => nb.id === b.id) ?? b) : b,
  );
  // Append any definitions not already in list (shouldn't happen, but safeguard)
  const existingIds = new Set(mergedBadges.map((b) => b.id));
  for (const nb of newBadges) {
    if (!existingIds.has(nb.id)) {
      mergedBadges.push(nb);
    }
  }

  return {
    ...currentState,
    level: newLevel,
    badges: mergedBadges,
    xpHistory: newHistory,
  };
}

// ─── All Badges ───────────────────────────────────────────────────────────────

/**
 * Returns all possible badges. Earned ones carry an `earnedAt` date;
 * unearned ones have `earnedAt: undefined`.
 */
export function getAllBadges(state: GamificationState): Badge[] {
  const earnedMap = new Map<string, string | undefined>(
    state.badges.map((b) => [b.id, b.earnedAt]),
  );
  return ALL_BADGE_DEFINITIONS.map((def) => ({
    ...def,
    earnedAt: earnedMap.get(def.id),
  }));
}

// ─── Initial State ────────────────────────────────────────────────────────────

export function createInitialState(): GamificationState {
  return {
    level: calculateLevel(0),
    badges: ALL_BADGE_DEFINITIONS.map((def) => ({ ...def })),
    streaks: { checkin: 0, goal: 0 },
    xpHistory: [],
  };
}

// ─── LocalStorage Persistence ─────────────────────────────────────────────────

export function loadGamificationState(): GamificationState {
  if (typeof window === 'undefined') return createInitialState();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return createInitialState();
    const parsed = JSON.parse(raw) as GamificationState;
    // Ensure all badge definitions are present (handles new badges added after first load)
    const existingIds = new Set(parsed.badges.map((b) => b.id));
    const allDefs = ALL_BADGE_DEFINITIONS;
    for (const def of allDefs) {
      if (!existingIds.has(def.id)) {
        parsed.badges.push({ ...def });
      }
    }
    // Ensure xpHistory exists (migration for older stored states)
    if (!Array.isArray(parsed.xpHistory)) {
      parsed.xpHistory = [];
    }
    return parsed;
  } catch {
    return createInitialState();
  }
}

export function saveGamificationState(state: GamificationState): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

// ─── Flexible Streak ──────────────────────────────────────────────────────────

/**
 * Calculates a "flexible streak" — the number of active days in the last 14 days.
 * Unlike strict consecutive-day streaks, this approach is more forgiving:
 * a streak is considered maintained if at least 10 of the last 14 days have check-ins.
 *
 * @param checkinDates - Array of ISO date strings (YYYY-MM-DD) when the user checked in
 * @returns The count of days with activity in the last 14 days (0–14)
 */
export function calculateFlexibleStreak(checkinDates: string[]): number {
  if (checkinDates.length === 0) return 0;

  const now = new Date();
  // Build a Set of unique date strings for fast lookup
  const dateSet = new Set(checkinDates);

  let activeDays = 0;

  for (let i = 0; i < 14; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);
    if (dateSet.has(dateStr)) {
      activeDays++;
    }
  }

  return activeDays;
}

/** Threshold: a flexible streak is "maintained" if >= 10 of the last 14 days are active */
export const FLEXIBLE_STREAK_THRESHOLD = 10;

/** Check whether the flexible streak is maintained */
export function isFlexibleStreakMaintained(checkinDates: string[]): boolean {
  return calculateFlexibleStreak(checkinDates) >= FLEXIBLE_STREAK_THRESHOLD;
}
